import React, { Fragment, useEffect, useState } from "react";
import { Link } from "react-router";

import {
  Column,
  Table as ReactTable,
  ColumnFiltersState,
  FilterFn,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  SortingState,
  ExpandedState,
  getExpandedRowModel,
} from '@tanstack/react-table';

import { rankItem } from '@tanstack/match-sorter-utils';

// Column Filter
const Filter = ({
  column
}: {
  column: Column<any, unknown>;
  table: ReactTable<any>;
}) => {
  const columnFilterValue = column.getFilterValue();

  return (
    <>
      <DebouncedInput
        type="text"
        value={(columnFilterValue ?? '') as string}
        onChange={value => column.setFilterValue(value)}
        placeholder="Search..."
        className="w-36 border shadow rounded"
        list={column.id + 'list'}
      />
      <div className="h-1" />
    </>
  );
};

// Global Filter
const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [debounce, onChange, value]);

  return (
    <input {...props} value={value} id="search-bar-0" className="form-control border-0 search" onChange={e => setValue(e.target.value)} />
  );
};

interface TableContainerProps {
  columns?: any;
  data?: any;
  isGlobalFilter?: any;
  handleTaskClick?: any;
  customPageSize?: any;
  tableClass?: any;
  theadClass?: any;
  trClass?: any;
  thClass?: any;
  divClass?: any;
  SearchPlaceholder?: any;
  handleLeadClick?: any;
  handleCompanyClick?: any;
  handleContactClick?: any;
  handleTicketClick?: any;
  enablePagination?: boolean;
  initialSorting?: SortingState;
}

const TableContainer = ({
  columns,
  data,
  isGlobalFilter,
  customPageSize,
  tableClass,
  theadClass,
  trClass,
  thClass,
  divClass,
  SearchPlaceholder,
  enablePagination = true,
  initialSorting = [],
}: TableContainerProps) => {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>(initialSorting);
  const [globalFilter, setGlobalFilter] = useState('');
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({ itemRank });
    return itemRank.passed;
  };

  const table = useReactTable({
    columns,
    data,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      columnFilters,
      sorting,
      globalFilter,
      expanded,
    },
    onExpandedChange: setExpanded,
    getSubRows: (row) => row.subRows,
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    ...(enablePagination && { getPaginationRowModel: getPaginationRowModel() }),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  const {
    getHeaderGroups,
    getRowModel,
    getCanPreviousPage,
    getCanNextPage,
    getPageOptions,
    setPageIndex,
    nextPage,
    previousPage,
    setPageSize,
    getState,
  } = table;

  useEffect(() => {
    if (enablePagination) {
      Number(customPageSize) && setPageSize(Number(customPageSize));
    }
  }, [customPageSize, setPageSize, enablePagination]);

  return (
    <Fragment>
      {isGlobalFilter && (
        <div className="row mb-3">
          <div className="card-body border border-dashed border-end-0 border-start-0">
            <form>
              <div className="row">
                <div className="col-sm-5">
                  <div className="search-box me-2 mb-2 d-inline-block col-12">
                    <DebouncedInput
                      value={globalFilter ?? ''}
                      onChange={(value) => setGlobalFilter(String(value))}
                      placeholder={SearchPlaceholder}
                    />
                    <i className="bx bx-search-alt search-icon"></i>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className={divClass}>
        <table className={`table table-hover ${tableClass}`}>
          <thead className={theadClass}>
            {getHeaderGroups().map((headerGroup) => (
              <tr className={trClass} key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={thClass}
                    {...{
                      onClick: header.column.getToggleSortingHandler(),
                    }}
                  >
                    {header.isPlaceholder ? null : (
                      <Fragment>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getCanSort()
                          ? {
                            asc: <i className="ri-arrow-up-s-line"></i>,
                            desc: <i className="ri-arrow-down-s-line"></i>,
                          }[header.column.getIsSorted() as string] ?? (
                            <i className="ri-arrow-up-down-line"></i>
                          )
                          : ''}
                        {header.column.getCanFilter() ? (
                          <div>
                            <Filter column={header.column} table={table} />
                          </div>
                        ) : null}
                      </Fragment>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {enablePagination && (
        <div className="row align-items-center mt-2 g-3 text-center text-sm-start">
          <div className="col-sm">
            <div className="text-muted">
              Mostrando
              <span className="fw-semibold ms-1">{getState().pagination.pageSize}</span> de{' '}
              <span className="fw-semibold">{data.length}</span> Resultados
            </div>
          </div>
          <div className="col-sm-auto">
            <ul className="pagination pagination-separated pagination-md justify-content-center justify-content-sm-start mb-0">
              <li className={!getCanPreviousPage() ? 'page-item disabled' : 'page-item'}>
                <Link to="#" className="page-link" onClick={previousPage}>
                  Anterior
                </Link>
              </li>
              {(() => {
                const currentPage = getState().pagination.pageIndex;
                const pageSize = getState().pagination.pageSize;
                const totalPages = Math.ceil(data.length / pageSize);
                const maxVisiblePages = 7; // Show max 7 page numbers

                // If total pages is less than maxVisiblePages, show all
                if (totalPages <= maxVisiblePages) {
                  return getPageOptions().map((item, key) => (
                    <Fragment key={key}>
                      <li className="page-item">
                        <Link
                          to="#"
                          className={currentPage === item ? 'page-link active' : 'page-link'}
                          onClick={() => setPageIndex(item)}
                        >
                          {item + 1}
                        </Link>
                      </li>
                    </Fragment>
                  ));
                }

                // Smart pagination with ellipsis
                const pages: (number | string)[] = [];
                const showEllipsis = totalPages > maxVisiblePages;

                if (currentPage <= 3) {
                  // Show first 5 pages + ellipsis + last page
                  for (let i = 0; i < 3; i++) {
                    pages.push(i);
                  }
                  if (showEllipsis) {
                    pages.push('ellipsis');
                  }
                  pages.push(totalPages - 1);
                } else if (currentPage >= totalPages - 4) {
                  // Show first page + ellipsis + last 5 pages
                  pages.push(0);
                  if (showEllipsis) {
                    pages.push('ellipsis');
                  }
                  for (let i = totalPages - 3; i < totalPages; i++) {
                    pages.push(i);
                  }
                } else {
                  // Show first page + ellipsis + current-1, current, current+1 + ellipsis + last page
                  pages.push(0);
                  pages.push('ellipsis');
                  for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                    pages.push(i);
                  }
                  pages.push('ellipsis');
                  pages.push(totalPages - 1);
                }

                return pages.map((item, key) => (
                  <Fragment key={key}>
                    {item === 'ellipsis' ? (
                      <li className="page-item disabled">
                        <span className="page-link">...</span>
                      </li>
                    ) : (
                      <li className="page-item">
                        <Link
                          to="#"
                          className={currentPage === item ? 'page-link active' : 'page-link'}
                          onClick={() => setPageIndex(item as number)}
                        >
                          {(item as number) + 1}
                        </Link>
                      </li>
                    )}
                  </Fragment>
                ));
              })()}
              <li className={!getCanNextPage() ? 'page-item disabled' : 'page-item'}>
                <Link to="#" className="page-link" onClick={nextPage}>
                  Próximo
                </Link>
              </li>
            </ul>
          </div>
        </div>
      )}
    </Fragment>
  );
};

export default TableContainer;
